import { Input, type InputProps } from '@mui/joy';
import { useState } from 'react';

import { type Mode, type NumericFormat } from './NumberInput.types';

import { MODE_PROPS, MODES } from './NumberInput.constants';

import toDecimal from '@/shared/utils/decimal';

import NumericFormatAdapter from './NumericFormatAdapter';

type NumberInput = string | number | null;

type Values = {
  asNumber: number | null;
  value: string | null;
};

const parsers = {
  in: (value: NumberInput | undefined) => (value === null ? '' : value),
  out: (value: string) =>
    value === ''
      ? {
          asNumber: null,
          value: null,
        }
      : {
          asNumber: toDecimal(value).toNumber(),
          value,
        },
};

const getModeProps = (mode: Mode = MODES.DEFAULT) => MODE_PROPS[mode];

// strings that fail number parsing are treated as a zero by this component
// but typing a decimal separator (, or .) or clearing ('') should not result in any alteration for the user
// example of an issue the extra state + util avoid: clearing input => insert 0 after cursor position
const representsSameNumber = (a: NumberInput, b: NumberInput) => toDecimal(a ?? 0).equal(b ?? 0);

type Props = {
  className?: string;
  defaultValue?: string | number | null;
  disabled?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onValueChange?: (values: Values) => void;
  mode?: Mode;
  name?: string;
  numericFormat?: NumericFormat;
  readOnly?: boolean;
  renderInput?: (props: InputProps) => React.ReactNode;
  size?: InputProps['size'];
  value?: string | number | null;
};

const NumberInput: React.FC<Props> = ({
  className,
  defaultValue,
  disabled,
  mode,
  name,
  numericFormat,
  onChange,
  onValueChange,
  readOnly,
  renderInput,
  size,
  value,
}) => {
  const [typedValue, setTypedValue] = useState<string | null>(null);

  const isControlled = value !== undefined;

  const displayedValue =
    isControlled && typedValue !== null && representsSameNumber(typedValue, value)
      ? typedValue
      : value;

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const values = parsers.out(e.target.value);

    onChange?.(e);
    onValueChange?.(values);
    setTypedValue(e.target.value);
  };

  const inputProps = {
    className,
    defaultValue: parsers.in(defaultValue),
    disabled,
    name,
    onChange: handleChange,
    readOnly,
    size,
    slotProps: {
      input: {
        ...getModeProps(mode),
        ...numericFormat,
        component: NumericFormatAdapter,
      },
    },
    value: parsers.in(displayedValue),
  } satisfies InputProps;

  return renderInput?.(inputProps) ?? <Input {...inputProps} />;
};

export default NumberInput;
